/**
 * Location Detection Utilities
 * 
 * Utility functions for detecting and deriving location-related data.
 * Includes timezone detection (browser-side) and currency/language derivation from region.
 */

import { Currency, Region } from '../types/region';
import {
  getMarketConfig,
  getMarketConfigForCountryCode,
  type MarketFeatureFlagState,
} from '../constants/markets';

/**
 * Detect browser timezone using Intl API
 * Returns IANA timezone string (e.g., "Australia/Sydney", "America/New_York")
 */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Failed to detect timezone:', error);
    return 'UTC'; // Fallback to UTC
  }
}

/**
 * Get language code from region
 * Maps region to appropriate language code
 * 
 * @param region - The user's region ('US' | 'AU')
 * @returns Language code (e.g., "en-US", "en-AU")
 */
export function getLanguageFromRegion(region?: Region): string {
  if (!region) {
    return 'en';
  }

  return getMarketConfig(region)?.locale ?? 'en';
}

/**
 * Map country code to currency
 * Currently supports US and AU, all others default to USD
 * 
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "AU", "US", "GB")
 * @returns Currency - 'USD' or 'AUD'
 */
export function getCurrencyFromCountryCode(
  countryCode: string | null | undefined,
  featureFlags: MarketFeatureFlagState = {},
): Currency {
  if (!countryCode) {
    return 'USD';
  }

  return getMarketConfigForCountryCode(countryCode, featureFlags)?.currency ?? 'USD';
}
